
import { useMutation, useQuery } from "@tanstack/react-query";
import { getGameListAPI, resumeGameAPI } from "../../../api/game";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import GreenBtn from "../../../components/button/SecondaryBtn";
import Loader from "../../../components/Loader/Loader";
import PlayGameModal from "../../../components/Modal/PlayGameModal";
import { useState } from "react";
import useToast from "../../../hooks/useToast";
import PlayBtn from "../../../assets/images/game-board/play-btn.svg";
import CmsBanner from "../../../components/Banner/CmsBanner";
import ResponsiveFlexGrid from "../../../components/FlexGrid/ResponsiveFlexGrid";

const GameList = () => {
  const { i18n, t } = useTranslation();
  const currentLanguage = i18n.language;
  const navigate = useNavigate();
  const [openPlayGameModal, setOpenPlayGameModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameToResume, setGameToResume] = useState(null);
  // always use usequery to get the package list
  const {
    data: gameList,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["gameList"],
    queryFn: getGameListAPI,
  });

  const { mutate: resumeGame, isPending: isResumingGame } = useMutation({
    mutationFn: resumeGameAPI,
    onSuccess: (data) => {
      useToast(data.message, "success");
      if (gameToResume && gameToResume?._id) {
        navigate(`/game/${gameToResume?._id}`);
      } else {
        navigate("/my-games");
      }
    },
    onError: (error) => {
      useToast(error.message, "error");
    },
  });

  const handleResumeGame = (game: any) => {
    resumeGame({ game_id: game?._id });
    setGameToResume(game);
  };

  const handleStartGame = (game: any) => {
    if (game?.status === "paused") {
      handleResumeGame(game);
      return;
    }
    // If game is completed, navigate to result page
    if (game?.status === "completed") {
      navigate(`/result/${game?._id}`);
      return;
    }
    if (game?.status === "pending") {
      setOpenPlayGameModal(true);
      setSelectedGame(game);
      return;
    }
    navigate(`/game/${game?._id}`);
  };

  const getBgColor = (index: number) => {
    return ["bg-[#B9F2FD]", "bg-[#F2BF7D]", "bg-[#C9D863]", "bg-[#97A4F2]", "bg-[#FFFBA1]", "bg-[#D76A4D]"][index % 6];
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className="wrapper flex-1">
        <CmsBanner title={t("gameList.gameListTitle")} description={t("gameList.gameListDescription")} />
        <section className="2xl:py-[3.5vw] lg:py-15 py-10 relative z-10 bg-cover bg-center bg-no-repeat bg-white">
          <div className="w-full  2xl:px-[0.833vw] xl:px-10 px-4 relative z-20">
            {/* Filter games into active and completed */}
            {(() => {
              const allGames = gameList?.data?.games || [];
              const activeGames = allGames.filter((game: any) => game?.status !== "completed");
              const completedGames = allGames.filter((game: any) => game?.status === "completed");

              if (allGames.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center">
                    <div className="text-center">
                      <h2 className="text-2xl font-bold">{t("gameList.noGamesFound")}</h2>
                    </div>
                    <GreenBtn to="/create-game" className="mt-4">
                      {t("gameList.createNewGame")}
                    </GreenBtn>
                  </div>
                );
              }

              return (
                <>
                  {/* Active Games Section */}
                  {activeGames.length > 0 ? (
                    <div className="">
                      <ResponsiveFlexGrid
                        items={activeGames}
                        maxColumns={{ xs: 2, sm: 3, md: 4, lg: 5 }}
                        gap={{ default: "1.5rem", lg: "1.25vw" }}
                        renderItem={(game: any, index: number) => (
                          <div
                            key={game._id}
                            className="h-full flex flex-col justify-center bg-primary-ivory rounded-lg 2xl:rounded-[0.52vw] border 2xl:border-[0.06vw] border-black-300 p-2 2xl:p-[0.52vw] gap-y-1.5 2xl:gap-y-[0.39vw]"
                          >
                            <div className={`h-[211px] w-full relative border-1 border-black-300 rounded-md ${getBgColor(index)}`}>
                              {/* <svg width="204" height="211" viewBox="0 0 204 211" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
                                <path
                                  d="M1.79973 172.158L5.32004 206.992C5.37165 207.503 5.80162 207.891 6.31497 207.891H17.7359H27.7595C27.8435 207.891 27.9271 207.902 28.0084 207.923L37.9135 210.469C37.9948 210.489 38.0784 210.5 38.1624 210.5H47.3563C47.4259 210.5 47.4953 210.493 47.5633 210.478L59.7859 207.891L74.1985 206.595C74.2567 206.59 74.3152 206.589 74.3734 206.594L89.5109 207.891H105.362C105.428 207.891 105.493 207.898 105.558 207.911L118.414 210.481C118.478 210.493 118.544 210.5 118.61 210.5H131.319C131.478 210.5 131.635 210.462 131.776 210.389L136.421 208.002C136.562 207.929 136.719 207.891 136.878 207.891H145.209C145.293 207.891 145.377 207.881 145.458 207.86L150.288 206.618C150.37 206.598 150.453 206.587 150.537 206.587H162.589C162.687 206.587 162.783 206.601 162.876 206.629L166.945 207.849C167.039 207.877 167.135 207.891 167.233 207.891H181.927C182.174 207.891 182.413 207.983 182.596 208.148L184.851 210.176C185.078 210.381 185.387 210.47 185.688 210.418L199.605 208.034C200.085 207.951 200.436 207.535 200.436 207.048V187.756C200.436 187.701 200.431 187.647 200.423 187.593L197.561 170.215C197.544 170.116 197.543 170.015 197.557 169.915L200.436 149.196V137.988C200.436 137.656 200.601 137.345 200.877 137.159L202.684 135.94C203.067 135.682 203.223 135.196 203.063 134.763L197.564 119.924C197.545 119.873 197.531 119.823 197.521 119.771C196.805 116.041 196.404 108.091 200.436 105.5C204.014 103.201 201.098 102.421 198.535 102.269C197.984 102.236 197.536 101.791 197.536 101.239V92.5663C197.536 92.4933 197.528 92.4206 197.512 92.3493L195.623 83.8521C195.488 83.2475 195.949 82.6739 196.568 82.6739C197.103 82.6739 197.536 82.2405 197.536 81.7059V71.2649C197.536 71.0505 197.605 70.8418 197.732 70.6695L200.16 67.3939C200.335 67.1579 200.397 66.8568 200.33 66.5706L197.823 55.8583C197.676 55.2309 198.153 54.6304 198.797 54.6304H202.336C202.888 54.6304 203.336 54.1827 203.336 53.6304V35.5969C203.336 35.2645 203.171 34.9538 202.895 34.7679L200.877 33.406C200.601 33.2201 200.436 32.9095 200.436 32.5771V21.249C200.436 20.9166 200.271 20.606 199.995 20.4201L197.977 19.0582C197.701 18.8723 197.536 18.5616 197.536 18.2292V5.91899C197.536 5.42723 197.178 5.00846 196.693 4.93136L185.211 3.1087L167.157 0.510199C167.11 0.503409 167.062 0.5 167.014 0.5H145.764C145.491 0.5 145.229 0.612094 145.04 0.810165L141.244 4.79473C141.077 4.96916 140.854 5.07772 140.614 5.10044L127.301 6.36106C127.241 6.36672 127.181 6.36699 127.121 6.36187L111.986 5.06522L99.0097 3.11975C98.9606 3.11239 98.911 3.1087 98.8614 3.1087H64.247C64.1732 3.1087 64.0996 3.11687 64.0276 3.13306L49.7443 6.3452C49.6723 6.36139 49.5987 6.36957 49.5249 6.36957H27.9913C27.9212 6.36957 27.8514 6.36221 27.7829 6.34761L1.70885 0.7925C1.03672 0.649302 0.423772 1.2111 0.508016 1.89314L1.76208 12.046C1.77784 12.1736 1.81804 12.2969 1.88048 12.4092L5.28499 18.5343C5.36759 18.6829 5.41094 18.8501 5.41094 19.0201V40.9348V67.0217L1.80721 95.549C1.79313 95.6605 1.79793 95.7735 1.82142 95.8834L5.36986 112.482C5.39699 112.609 5.39916 112.74 5.37626 112.867L1.81927 132.705C1.79724 132.828 1.79841 132.954 1.82272 133.077L5.37471 150.97C5.39865 151.09 5.40014 151.214 5.37909 151.335L1.80942 171.887C1.79384 171.976 1.79058 172.068 1.79973 172.158Z"
                                  fill={
                                    [
                                      "#B9F2FD",
                                      "#F2BF7D",
                                      "#C9D863",
                                      "#97A4F2",
                                      "#FFFBA1",
                                      "#D76A4D",
                                    ][index % 6]
                                  }
                                  stroke="black"
                                />
                              </svg> */}

                              <h3 className="2xl:text-[1.95vw] md:text-3xl text-2xl md:leading-8 leading-7 text-black-300 font-bold text-center lg:px-[0.833vw] px-4 absolute left-0 right-0 top-1/2 -translate-y-1/2">
                                {game?.game_name}
                              </h3>
                            </div>
                            <button
                              onClick={() => handleStartGame(game)}
                              className="flex justify-center cursor-pointer transition-shadow relative"
                            >
                              <img src={PlayBtn} className="w-full" />
                              <p className="absolute text-center left-0 right-0 top-1/2 -translate-y-1/2 text-base lg:text-lg 2xl:text-[1.17vw] font-bold text-black-300">
                                {game.status === "paused" ? t("gameList.resumeGame") : t("gameList.startGame")}
                              </p>
                            </button>
                          </div>
                        )}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center ">
                      <h2 className="text-2xl font-bold">{t("gameList.noActiveGamesFound")}</h2>
                      <GreenBtn to="/create-game" className="mt-4">
                        {t("gameList.createNewGame")}
                      </GreenBtn>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </section>
      </div>
      <PlayGameModal
        isOpen={openPlayGameModal}
        onClose={() => setOpenPlayGameModal(false)}
        selectedGame={selectedGame}
      />
    </>
  );
};

export default GameList;

// {/* Completed Games Section */}
// {completedGames.length > 0 && (
//   <div>
//     <h2 className="font-garamond font-bold 2xl:text-[1.5vw] text-2xl text-black-900 lg:mb-[1.25vw] mb-5">
//       Completed Games
//     </h2>
//     <div className="grid lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 lg:gap-[1.25vw] gap-6">
//       {completedGames.map((game: any) => (
//         <div
//           key={game._id}
//           className="card lg:pb-[0.729vw] pb-3.5 lg:pt-[0.625vw] pt-3 h-full flex flex-col justify-center card-shape relative shadow-[inset_0_0_24px_0_#5900E8] [background:padding-box_linear-gradient(#5900E8,#7364FF),_border-box_linear-gradient(#7364FF,#5900E8)]"
//         >
//           {/* Completed badge */}
//           {/* <span className="absolute top-2 end-2 lg:top-5 lg:end-4 bg-primary text-white text-xs lg:text-sm font-semibold px-2 py-1 lg:px-3 lg:py-1.5 rounded-full shadow-md z-10">
//             Completed
//           </span> */}
//           <div className="lg:mx-[0.625vw] mx-3 lg:mb-[0.625vw] mb-3 lg:px-[0.625vw] px-3 flex items-center justify-center lg:py-[3.542vw] py-12 h-full flex-col card-inner-shape bg-white/20 lg:shadow-[inset_0_0_1.25vw_0_#C3DC7880] shadow-[inset_0_0_24px_0_#C3DC7880]">
//             <h3 className="font-semibold 2xl:text-[2.083vw]/tight md:text-3xl text-2xl md:leading-8 leading-7 text-white font-garamond text-center lg:px-[0.833vw] px-4">
//               {game?.game_name}
//             </h3>
//           </div>
//           <button
//             onClick={() => handleStartGame(game)}
//             className="flex justify-center lg:mx-[1.354vw] mx-5.5 lg:gap-[0.521vw] gap-2 items-center text-black-300 font-semibold 2xl:text-[1.042vw] text-lg lg:py-[0.417vw] py-2 lg:px-[0.729vw] md:px-3.5 px-2.5 rounded-full bg-purple-fill lg:border-[0.3vw] border-5 border-transparent [background:padding-box_linear-gradient(#91A459,#C3DC78),_border-box_linear-gradient(#C3DC78,#91A459)] cursor-pointer hover:shadow-lg transition-shadow"
//           >
//             View Result
//           </button>
//         </div>
//       ))}
//     </div>
//   </div>
// )}
